import type { NewsItem } from "@/mocks/news";
import { newsList, timelineUpdates } from "@/mocks/news";
import Newsletter from "@/components/feature/Newsletter";

type Props = {
  onOpen: (a: NewsItem) => void;
  onKeyword: (w: string) => void;
};

const topics = [
  "跨年音乐季",
  "红毯高定造型",
  "高分悬疑剧",
  "户外综艺",
  "折叠屏新品",
  "动画电影票房",
  "手冲咖啡",
  "慢综艺收官",
  "新专辑录制",
  "颁奖礼预测",
];

const specials = [
  { tag: "深度专题", title: "年末综艺大赏：谁承包了这一年的快乐与泪水", img: "https://readdy.ai/api/search-image?query=bright%20tv%20variety%20studio%20stage%20confetti%20celebration%20colorful%20spotlights%20warm%20golden%20lively%20energy%20editorial&width=1200&height=760&seq=cap-special-var-01&orientation=landscape", cat: "variety" },
  { tag: "年度盘点", title: "年度高分片单：这些值得二刷的作品别错过", img: "https://readdy.ai/api/search-image?query=cinema%20film%20reel%20warm%20spotlight%20movie%20posters%20golden%20bokeh%20elegant%20editorial%20retro&width=1200&height=760&seq=cap-special-film-01&orientation=landscape", cat: "film" },
];

function toArticle(cat: string) {
  return newsList.find((n) => n.category === cat && n.featured) || newsList.find((n) => n.category === cat) || newsList[0];
}

export default function Extras({ onOpen, onKeyword }: Props) {
  return (
    <>
      {/* 热聊话题 */}
      <section className="bg-background-100 py-10">
        <div className="mx-auto max-w-7xl px-4 md:px-6">
          <h2 className="flex items-center gap-2 font-heading text-2xl font-black text-foreground-950">
            <i className="ri-chat-3-fill text-accent-600"></i> 今日热聊话题
          </h2>
          <p className="mt-1.5 text-sm text-foreground-500">点按话题即可搜索相关快讯，紧跟当前讨论热度。</p>
          <div className="mt-6 flex flex-wrap items-center gap-2.5">
            {topics.map((t, i) => (
              <button
                key={t}
                onClick={() => onKeyword(t)}
                className={`group flex items-center gap-2 rounded-full border px-4 py-2 text-sm transition-colors cursor-pointer ${
                  i % 4 === 0
                    ? "border-primary-200 bg-primary-100/70 text-primary-800 hover:bg-primary-100"
                    : i % 4 === 1
                    ? "border-accent-200 bg-accent-100/70 text-accent-900 hover:bg-accent-100"
                    : "border-background-300 bg-background-50 text-foreground-700 hover:border-secondary-400 hover:bg-secondary-100"
                }`}
              >
                <i className="ri-price-tag-3-line"></i>
                {t}
                <i className="ri-arrow-right-up-line opacity-0 transition-opacity group-hover:opacity-100"></i>
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* 现场直击 专题 */}
      <section className="bg-background-50 py-12">
        <div className="mx-auto max-w-7xl px-4 md:px-6">
          <h2 className="flex items-center gap-2 font-heading text-2xl font-black text-foreground-950">
            <i className="ri-live-fill text-primary-500"></i> 现场直击 · 专题策划
          </h2>
          <p className="mt-1.5 text-sm text-foreground-500">编辑部精选深度内容，一次看懂热门事件的来龙去脉。</p>
          <div className="mt-6 grid grid-cols-1 gap-5 lg:grid-cols-2">
            {specials.map((s) => {
              const art = toArticle(s.cat);
              return (
                <button
                  key={s.tag}
                  onClick={() => onOpen(art)}
                  className="group relative block w-full cursor-pointer overflow-hidden rounded-2xl text-left"
                >
                  <div className="h-64 w-full md:h-80 overflow-hidden">
                    <img src={s.img} alt={s.title} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" loading="lazy" />
                  </div>
                  <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/85 via-foreground-950/20 to-transparent"></div>
                  <div className="absolute inset-x-0 bottom-0 p-5 md:p-6">
                    <span className="inline-block rounded-md bg-accent-500 px-2.5 py-0.5 text-xs font-medium text-background-50">{s.tag}</span>
                    <h3 className="mt-3 font-heading text-lg font-bold leading-snug text-background-50 md:text-2xl">{s.title}</h3>
                    <span className="mt-3 inline-flex items-center gap-1.5 text-sm font-medium text-accent-400">
                      立即阅读 <i className="ri-arrow-right-line transition-transform group-hover:translate-x-1"></i>
                    </span>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      </section>

      {/* 追更时间线 */}
      <section className="bg-background-100 py-12">
        <div className="mx-auto max-w-7xl px-4 md:px-6">
          <div className="grid grid-cols-1 gap-8 lg:grid-cols-2">
            <div>
              <h2 className="flex items-center gap-2 font-heading text-2xl font-black text-foreground-950">
                <i className="ri-time-line text-primary-500"></i> 今日追更时间线
              </h2>
              <p className="mt-2 text-sm leading-relaxed text-foreground-600">
                热门事件进展第一时间同步，按时间线梳理今日重要节点，吃瓜追更不错过任何环节。实时更新随时刷新。
              </p>
              <div className="mt-4 rounded-xl bg-accent-100/70 p-4 text-sm text-accent-900">
                <i className="ri-question-line mr-1"></i>
                想看某条动态的完整报道？点击时间线上的快讯，或在资讯流中搜索对应关键词。
              </div>
            </div>
            <div className="relative pl-5">
              <span className="absolute left-[7px] top-1 bottom-1 w-px bg-background-300"></span>
              <ul className="space-y-5">
                {timelineUpdates.map((u, i) => (
                  <li key={i} className="relative pl-5">
                    <span
                      className={`absolute left-[-13px] top-1 flex h-3.5 w-3.5 items-center justify-center rounded-full border-2 border-background-50 ${
                        i === 0 ? "cap-dot relative bg-primary-500" : "bg-accent-500"
                      }`}
                    ></span>
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-mono text-sm font-semibold text-primary-600">{u.time}</span>
                      <span className="rounded bg-secondary-100 px-1.5 py-0.5 text-[11px] text-secondary-900">{u.label}</span>
                    </div>
                    <p className="mt-1 text-sm leading-relaxed text-foreground-800">{u.text}</p>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* 订阅横幅 */}
      <section id="subscribe" className="scroll-mt-24 bg-background-50 px-4 py-14 md:px-6">
        <div className="mx-auto max-w-5xl overflow-hidden rounded-3xl bg-gradient-to-br from-primary-600 via-primary-500 to-accent-500 px-6 py-12 text-center md:px-14">
          <span className="inline-flex items-center gap-1.5 rounded-full bg-background-50/15 px-3 py-1 text-xs font-medium text-background-50">
            <i className="ri-notification-4-line"></i> 免费订阅 · 随时退订
          </span>
          <h2 className="mt-4 font-heading text-2xl font-black text-background-50 md:text-3xl">
            新鲜快讯不错过，订阅即可抢先看
          </h2>
          <p className="mx-auto mt-3 max-w-xl text-sm leading-relaxed text-background-100">
            留下邮箱，海角爆每天为你整理热门事件进展与新鲜快讯，第一时间同步重要节点。
          </p>
          <div className="mt-7">
            <Newsletter />
          </div>
        </div>
      </section>
    </>
  );
}